import { useCallback, useRef } from 'react';

function useLinkReachabilityProbe({
  timeout = 4000,
  method = 'HEAD',
  optimistic = false,
  onUnreachable = () => {},
  onProbeStart = () => {},
  onProbeEnd = () => {}
} = {}) {
  const inflight = useRef(new Set());

  const probe = useCallback(async (url) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    try {
      await fetch(url, {
        method,
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal
      });

      return true;
    } catch (e) {
      if (e.name === 'AbortError') {
        return optimistic;
      }

      return false;
    } finally {
      clearTimeout(timer);
    }
  }, [timeout, method, optimistic]);

  const onClick = useCallback(async (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const anchor = event.currentTarget.closest('a');

    if (!anchor) {
      return;
    }

    const href = anchor.href;

    if (!/^https?:\/\//i.test(href)) {
      return;
    }

    if (inflight.current.has(href)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    inflight.current.add(href);
    onProbeStart(href);

    const reachable = await probe(href);

    inflight.current.delete(href);
    onProbeEnd(href, reachable);

    if (reachable) {
      window.open(href, anchor.target || '_blank', 'noopener,noreferrer');
    } else {
      onUnreachable(href, event);
    }
  }, [probe, onProbeStart, onProbeEnd, onUnreachable]);

  return onClick;
}

export default useLinkReachabilityProbe;